import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import {
  portfolioService,
  type PortfolioBlogPostDto,
  type PortfolioSettingsDto,
} from '@/services/portfolio';
import { blogRichTextPreviewHtml } from '@/utils/blogPreview';
import { FileText, Eye, Trash2, Pencil, PlusCircle, LayoutPanelLeft, Rows3 } from 'lucide-react';
import {
  PromoCard,
  PromoCardBody,
  PromoCardDesc,
  PromoCardHeader,
  PromoCardTitle,
} from '@/components/promo/PromoCard';
import { getPublicPortfolioUrl } from '@/utils/hostOrigins';

type BlogDraft = {
  id?: string;
  slug: string;
  title: string;
  excerpt: string;
  body: string;
  featuredImageUrl: string;
  isPublished: boolean;
};

type ViewMode = 'edit' | 'preview' | 'split';

const emptyBlogDraft = (): BlogDraft => ({
  slug: '',
  title: '',
  excerpt: '',
  body: '',
  featuredImageUrl: '',
  isPublished: false,
});

const TeacherBlogsPage: React.FC = () => {
  const { toast } = useToast();
  const [blogPosts, setBlogPosts] = useState<PortfolioBlogPostDto[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioSettingsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [blogDraft, setBlogDraft] = useState<BlogDraft>(() => emptyBlogDraft());
  const [blogSaving, setBlogSaving] = useState(false);
  const [deletingBlogId, setDeletingBlogId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('split');

  const publicUrl = useMemo(
    () => getPublicPortfolioUrl(portfolio?.slug ?? ''),
    [portfolio?.slug]
  );

  const excerptPreview = useMemo(() => blogRichTextPreviewHtml(blogDraft.excerpt), [blogDraft.excerpt]);
  const bodyPreview = useMemo(() => blogRichTextPreviewHtml(blogDraft.body), [blogDraft.body]);

  useEffect(() => {
    void loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [blogs, pf] = await Promise.all([
        portfolioService.listBlogPosts(),
        portfolioService.getMyPortfolio().catch(() => null),
      ]);
      setBlogPosts(Array.isArray(blogs) ? blogs : []);
      setPortfolio(pf);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to load blog posts',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetBlogDraft = () => {
    setBlogDraft(emptyBlogDraft());
  };

  const startEditBlog = (post: PortfolioBlogPostDto) => {
    setBlogDraft({
      id: post.id,
      slug: post.slug,
      title: post.title,
      excerpt: post.excerpt ?? '',
      body: post.body ?? '',
      featuredImageUrl: post.featuredImageUrl ?? '',
      isPublished: post.isPublished,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveBlog = async () => {
    const title = blogDraft.title.trim();
    if (!title) {
      toast({
        title: 'Title required',
        description: 'Give your post a title.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setBlogSaving(true);
      if (blogDraft.id) {
        const updated = await portfolioService.updateBlogPost(blogDraft.id, {
          title,
          excerpt: blogDraft.excerpt,
          body: blogDraft.body,
          featuredImageUrl: blogDraft.featuredImageUrl.trim(),
          isPublished: blogDraft.isPublished,
        });
        setBlogPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        toast({ title: 'Saved', description: 'Post updated. The server sanitizes HTML for safety.' });
      } else {
        const created = await portfolioService.createBlogPost({
          slug: blogDraft.slug.trim() || undefined,
          title,
          excerpt: blogDraft.excerpt.trim() ? blogDraft.excerpt : undefined,
          body: blogDraft.body.trim() ? blogDraft.body : undefined,
          featuredImageUrl: blogDraft.featuredImageUrl.trim() || undefined,
          isPublished: blogDraft.isPublished,
        });
        setBlogPosts((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
        resetBlogDraft();
        toast({ title: 'Created', description: 'Post added. HTML is sanitized when saved.' });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to save post',
        variant: 'destructive',
      });
    } finally {
      setBlogSaving(false);
    }
  };

  const handleDeleteBlog = async (postId: string) => {
    if (!window.confirm('Delete this post permanently?')) {
      return;
    }
    try {
      setDeletingBlogId(postId);
      await portfolioService.deleteBlogPost(postId);
      setBlogPosts((prev) => prev.filter((p) => p.id !== postId));
      if (blogDraft.id === postId) {
        resetBlogDraft();
      }
      toast({ title: 'Deleted', description: 'Post removed.' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'Failed to delete post',
        variant: 'destructive',
      });
    } finally {
      setDeletingBlogId(null);
    }
  };

  const showEditor = viewMode === 'edit' || viewMode === 'split';
  const showPreview = viewMode === 'preview' || viewMode === 'split';

  return (
    <PromoCard>
      <PromoCardHeader>
        <PromoCardTitle icon={<FileText size={20} strokeWidth={2} />}>Blog</PromoCardTitle>
        <PromoCardDesc>
          Publish posts students and parents actually see — without a separate CMS.
        </PromoCardDesc>
      </PromoCardHeader>
      <PromoCardBody className="space-y-4">
          {!portfolio && !loading && (
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-50">
              Could not load Portfolio Site settings. You can still edit posts; enable the <strong>Portfolio Site</strong>{' '}
              feature to get your public link here.
            </div>
          )}

          {publicUrl && (
            <div className="rounded border border-slate-200 bg-slate-100 px-3 py-2 space-y-1">
              <p className="text-xs text-gray-400">Public site</p>
              <p className="text-sm text-foreground break-all">{publicUrl}</p>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-foreground font-medium">
                {blogDraft.id ? `Editing: ${blogDraft.slug}` : 'New post'}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={viewMode === 'edit' ? 'default' : 'outline'}
                  className="border-white/20"
                  onClick={() => setViewMode('edit')}
                >
                  <Rows3 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={viewMode === 'split' ? 'default' : 'outline'}
                  className="border-white/20"
                  onClick={() => setViewMode('split')}
                >
                  <LayoutPanelLeft className="w-4 h-4 mr-1" />
                  Split
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={viewMode === 'preview' ? 'default' : 'outline'}
                  className="border-white/20"
                  onClick={() => setViewMode('preview')}
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Preview
                </Button>
              </div>
            </div>

            {!blogDraft.id && (
              <div>
                <Label className="text-foreground">Slug (optional)</Label>
                <Input
                  value={blogDraft.slug}
                  onChange={(event) => setBlogDraft((prev) => ({ ...prev, slug: event.target.value }))}
                  placeholder="derived-from-title"
                  className="bg-slate-50 border-slate-200 text-foreground font-mono text-sm"
                />
              </div>
            )}
            {blogDraft.id && (
              <div>
                <Label className="text-foreground">Slug</Label>
                <Input value={blogDraft.slug} readOnly disabled className="bg-slate-100 border-slate-200 text-gray-400" />
              </div>
            )}
            <div>
              <Label className="text-foreground">Title</Label>
              <Input
                value={blogDraft.title}
                onChange={(event) => setBlogDraft((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="Post title"
                className="bg-slate-50 border-slate-200 text-foreground"
              />
            </div>

            <div>
              <Label className="text-foreground">Cover image URL</Label>
              <Input
                value={blogDraft.featuredImageUrl}
                onChange={(event) =>
                  setBlogDraft((prev) => ({ ...prev, featuredImageUrl: event.target.value }))
                }
                placeholder="https://… (shown in blog listings and at top of article)"
                className="bg-slate-50 border-slate-200 text-foreground font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional https image. Leave empty to use the first inline &lt;img&gt; in excerpt or body.
              </p>
            </div>

            <div className={showEditor && showPreview ? 'grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-6' : ''}>
              {showEditor && (
                <div className="space-y-4">
                  <div>
                    <Label className="text-foreground">Excerpt (plain text or HTML)</Label>
                    <Textarea
                      value={blogDraft.excerpt}
                      onChange={(event) => setBlogDraft((prev) => ({ ...prev, excerpt: event.target.value }))}
                      placeholder="Short teaser for the listing…"
                      className="bg-slate-50 border-slate-200 text-foreground font-mono text-sm min-h-[100px]"
                    />
                  </div>
                  <div>
                    <Label className="text-foreground">Body (HTML)</Label>
                    <Textarea
                      value={blogDraft.body}
                      onChange={(event) => setBlogDraft((prev) => ({ ...prev, body: event.target.value }))}
                      placeholder='<p>Use tags like &lt;p&gt;, &lt;h2&gt;, &lt;strong&gt;, &lt;ul&gt;, &lt;a href="https://…"&gt;.</p>'
                      className="bg-slate-50 border-slate-200 text-foreground font-mono text-sm min-h-[280px]"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Scripts, iframes, and dangerous attributes are stripped on save (and in this preview).
                    </p>
                  </div>
                </div>
              )}

              {showPreview && (
                <div className="rounded-lg border border-slate-200 bg-slate-100 p-4 space-y-6 min-h-[200px]">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-purple-300 mb-2">Listing teaser</p>
                    <div
                      className="text-sm text-gray-300 prose-invert [&_a]:text-purple-300 [&_p]:my-2"
                      dangerouslySetInnerHTML={{ __html: excerptPreview || '<p class="text-gray-500">(empty)</p>' }}
                    />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-purple-300 mb-2">Article</p>
                    <div
                      className="text-gray-200 text-[15px] leading-relaxed [&_h2]:text-xl [&_h3]:text-lg [&_h2]:font-semibold [&_h3]:font-semibold [&_p]:my-3 [&_ul]:my-3 [&_a]:text-purple-300 [&_a]:underline [&_blockquote]:border-l [&_blockquote]:border-purple-500 [&_blockquote]:pl-4 [&_img]:max-w-full [&_img]:rounded [&_pre]:overflow-x-auto"
                      dangerouslySetInnerHTML={{
                        __html: bodyPreview || '<p class="text-gray-500">(empty body)</p>',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={blogDraft.isPublished}
                onChange={(event) => setBlogDraft((prev) => ({ ...prev, isPublished: event.target.checked }))}
                className="rounded border-white/20 bg-slate-50 accent-purple-500"
              />
              Published (visible on public portfolio when Portfolio Site is published)
            </label>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button type="button" onClick={handleSaveBlog} disabled={blogSaving || loading}>
                {blogSaving ? 'Saving…' : blogDraft.id ? 'Save changes' : 'Create post'}
              </Button>
              <Button type="button" variant="outline" className="border-white/20" onClick={() => resetBlogDraft()} disabled={loading}>
                <PlusCircle className="w-4 h-4 mr-2" />
                New post
              </Button>
              <Button type="button" variant="outline" className="border-white/20" onClick={() => void loadData()} disabled={loading}>
                Refresh list
              </Button>
            </div>
          </div>

          {loading ? (
            <p className="text-gray-400 text-sm">Loading posts…</p>
          ) : blogPosts.length === 0 ? (
            <p className="text-gray-400 text-sm">No posts yet.</p>
          ) : (
            <div className="space-y-2">
              {blogPosts.map((post) => (
                <div
                  key={post.id}
                  className="rounded-lg border border-slate-200 bg-slate-100 p-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-foreground font-medium truncate">{post.title}</p>
                    <p className="text-xs text-gray-500 font-mono break-all">{post.slug}</p>
                    <p className="text-[11px] text-gray-500 mt-1">Updated {new Date(post.updatedAt).toLocaleString()}</p>
                    <span
                      className={`inline-block mt-2 text-[11px] px-2 py-0.5 rounded border ${
                        post.isPublished
                          ? 'bg-green-500/15 text-green-300 border-green-500/25'
                          : 'bg-slate-50 text-gray-400 border-slate-200'
                      }`}
                    >
                      {post.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button type="button" size="sm" variant="outline" className="border-white/20" onClick={() => startEditBlog(post)}>
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={() => void handleDeleteBlog(post.id)}
                      disabled={deletingBlogId === post.id}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      {deletingBlogId === post.id ? '…' : 'Delete'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </PromoCardBody>
    </PromoCard>
  );
};

export default TeacherBlogsPage;
